import { Link } from 'react-router-dom';
import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Instagram, Linkedin, Mail, MessageCircle } from 'lucide-react';
import { FOOTER, FORM } from '@/constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('You\u2019re on the list. Welcome.');
      setEmail('');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid={FOOTER.container} className="bg-[#1C1C1C] text-[#F4EFEA]">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="eyebrow text-[#C9A24D]">Eureka1</div>
            <h3 className="font-serif-display text-3xl sm:text-4xl mt-5 leading-tight">
              Reliable support,<br />
              <span className="font-serif-italic italic text-[#C9A24D]">handled with care.</span>
            </h3>
            <p className="mt-6 text-[#E6CFC7]/90 max-w-md leading-relaxed">
              An operations and systems practice for service-based businesses
              ready to grow without losing the experience that got them here.
            </p>

            <form onSubmit={subscribe} data-testid={FOOTER.newsletter} className="mt-10 max-w-md">
              <label className="eyebrow text-[#C9A24D]">Operations Journal</label>
              <div className="flex items-end gap-3 mt-3 border-b border-[#F4EFEA]/30 pb-2 focus-within:border-[#C9A24D] transition-colors">
                <input
                  data-testid={FORM.newsletterEmail}
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 bg-transparent border-none outline-none text-[#F4EFEA] placeholder:text-[#F4EFEA]/40 py-2"
                />
                <button
                  type="submit"
                  data-testid={FORM.newsletterSubmit}
                  disabled={loading}
                  className="text-xs uppercase tracking-[0.2em] text-[#C9A24D] hover:text-[#F4EFEA] transition-colors pb-2"
                >
                  {loading ? 'Sending\u2026' : 'Subscribe'}
                </button>
              </div>
              <p className="text-xs text-[#E6CFC7]/60 mt-3 font-serif-italic italic">
                One thoughtful note a month. Never noise.
              </p>
            </form>
          </div>

          <div className="lg:col-span-3 lg:col-start-7">
            <div className="eyebrow text-[#C9A24D]">Navigate</div>
            <ul className="mt-6 space-y-3 text-sm text-[#F4EFEA]/80">
              <li><Link className="hover:text-[#C9A24D] transition-colors" to="/about">About Hallie</Link></li>
              <li><Link className="hover:text-[#C9A24D] transition-colors" to="/services">Services</Link></li>
              <li><Link className="hover:text-[#C9A24D] transition-colors" to="/portfolio">Portfolio</Link></li>
              <li><Link className="hover:text-[#C9A24D] transition-colors" to="/eureka1">Eureka1</Link></li>
              <li><Link className="hover:text-[#C9A24D] transition-colors" to="/blog">Journal</Link></li>
              <li><Link className="hover:text-[#C9A24D] transition-colors" to="/contact">Contact</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="eyebrow text-[#C9A24D]">Services</div>
            <ul className="mt-6 space-y-3 text-sm text-[#F4EFEA]/80">
              <li><Link className="hover:text-[#C9A24D] transition-colors" to="/services/virtual-support">Virtual Support</Link></li>
              <li><Link className="hover:text-[#C9A24D] transition-colors" to="/services/website-design">Website Design</Link></li>
              <li><Link className="hover:text-[#C9A24D] transition-colors" to="/services/seo-optimization">SEO</Link></li>
              <li><Link className="hover:text-[#C9A24D] transition-colors" to="/services/social-media-management">Social Media</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-[#F4EFEA]/15 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
          <p className="text-xs tracking-wider text-[#F4EFEA]/50">
            © {new Date().getFullYear()} Hallie &middot; Eureka1. All rights reserved.
          </p>
          <div className="flex items-center gap-5 text-[#F4EFEA]/70">
            <a href="mailto:imhallie09@gmail.com" aria-label="Email" data-testid="footer-email" className="hover:text-[#C9A24D] transition-colors"><Mail size={18}/></a>
            <a href="https://wa.me/639638096014" target="_blank" rel="noreferrer" aria-label="WhatsApp" data-testid="footer-whatsapp" className="hover:text-[#C9A24D] transition-colors"><MessageCircle size={18}/></a>
            <a href="https://instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-[#C9A24D] transition-colors"><Instagram size={18}/></a>
            <a href="https://linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="hover:text-[#C9A24D] transition-colors"><Linkedin size={18}/></a>
          </div>
        </div>
      </div>
    </footer>
  );
}
